<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { X, Search } from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import DateInput from './DateInput.vue'
import { toGerman, fromGerman, type IsoDate } from '@/utils/dateUtils'
import { CATEGORIES } from './planner.js'
import {
  FILTER_VERSION, todayIso, rangeOf, rangeFrom, defaultRange, defaultFilter,
  bookedInRange, freeInRange, isAvailabilityProject,
  type ResourceFilter,
} from './resourceFilter'

const props = defineProps<{
  open: boolean
  project: any
  users: any[]
  bookings: any[]
  projects: any[]
  resourceFilter: any
}>()

const emit = defineEmits<{
  apply: [filter: ResourceFilter]
  cancel: []
}>()

// Ein Kriterium ist entweder ein Projekt ("p:<id>"), der Zeitraum ("time") oder
// "alle" ("all"). Mehrere Kriterien verknuepfen sich als ODER.
type CriterionKey = string

const activeCriteria = ref<Set<CriterionKey>>(new Set())
// Personen, die zusaetzlich zu den Kriterien ausgewaehlt sind — und nach dem
// Aufloesen eines Kriteriums auch dessen bisherige Personen.
const manualAdded = ref<Set<number>>(new Set())

const dateFrom = ref<IsoDate>('')
const dateTo = ref<IsoDate>('')
const search = ref('')

// Ob dieses Projekt einen eigenen Filter hat oder auf dem Standard steht. Der
// Zustand ist von aussen sonst nicht erkennbar — deshalb haengt der Knopf
// "Auf Standard zuruecksetzen" sichtbar daran.
const hasOwnFilter = computed(() => props.resourceFilter?.mode === 'custom')

// Gesetzt, solange der Nutzer "Auf Standard zurücksetzen" gedrückt und seither
// nichts angefasst hat. Erst "Anwenden" verwirft den eigenen Filter wirklich.
const wantsDefault = ref(false)

// Startzustand: nur das eigene Projekt. "Frei" ist bewusst NICHT angehakt —
// im Echtbetrieb ist etwa die Haelfte der Belegschaft im Zeitraum frei, und die
// stuende sonst bei jedem Oeffnen mit in der Liste. Wer einplanen will, hakt es
// mit einem Klick dazu; die Zeitraumfelder stehen dafuer schon richtig.
function resetToDefaults() {
  const today = todayIso()
  const range = defaultRange(today)
  activeCriteria.value = new Set([`p:${props.project.id}`])
  manualAdded.value = new Set()
  dateFrom.value = range.from
  dateTo.value = range.to
}

// Die Kategorien, deren Unterprojekte angezeigt werden. Leere Auswahl heisst
// hier NICHT "nichts zeigen", sondern wird beim Speichern zu null - der
// Standard, bei dem alles sichtbar ist.
const visibleCategories = ref<Set<string>>(new Set(
  props.resourceFilter?.categories ?? Object.keys(CATEGORIES)
))

const hasSubprojects = computed(() =>
  (props.projects ?? []).some((p: any) => p.parent_id === props.project?.id)
)

watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  const today = todayIso()
  const stored = props.resourceFilter
  search.value = ''
  wantsDefault.value = false
  // Die Kategorienauswahl steht ausserhalb von mode und wird deshalb hier
  // eigenstaendig gelesen; ohne Eintrag sind alle sichtbar.
  visibleCategories.value = new Set(stored?.categories ?? Object.keys(CATEGORIES))

  if (stored?.mode === 'custom') {
    manualAdded.value = new Set(stored.added ?? [])
    const keys: CriterionKey[] = (stored.projects ?? []).map((id: number) => `p:${id}`)
    if (stored.all) keys.push('all')
    if (stored.free) keys.push('free')
    activeCriteria.value = new Set(keys)
    const range = stored.range ? rangeOf(stored.range, today) : defaultRange(today)
    dateFrom.value = range.from
    dateTo.value = range.to ?? defaultRange(today).to
    return
  }

  // Noch nie angewendet: mit dem eigenen Projekt starten.
  resetToDefaults()
})

const sortedFilteredUsers = computed(() => {
  const q = search.value.toLowerCase()
  return [...props.users]
    .filter(u => !q || `${u.firstname} ${u.lastname} ${u.short}`.toLowerCase().includes(q))
    .sort((a, b) => a.firstname.localeCompare(b.firstname, 'de'))
})

// Das eigene Projekt steht IMMER in der Zeile — auch wenn es noch keine Buchung
// hat oder nur geplant ist (status 2). Sonst fehlt ausgerechnet das Kaestchen,
// von dem aus das Fenster geoeffnet wurde.
const projectsWithBookings = computed(() => {
  const ids = new Set(props.bookings.map((b: any) => b.project_id))
  const own = props.project?.id
  // Die Verfuegbarkeits-Projekte stehen immer in der Zeile, auch ohne Buchung:
  // *verfuegbar ist derzeit leer, wird aber gebraucht, um es zu befuellen.
  return props.projects.filter((p: any) =>
    p.id !== 0 && (p.id === own || isAvailabilityProject(p) || (ids.has(p.id) && p.status === 0))
  )
})

// --- Mengen hinter den Kriterien ------------------------------------------
// Die Auswertung liegt in resourceFilter.ts, damit Vorschau und spaetere Anzeige
// im Planer garantiert dieselbe Rechnung benutzen.

// Der Zeitraum gilt fuer alle Kriterien, auch fuer die Projekt-Kaestchen.
function idsForCriterion(key: CriterionKey): number[] {
  if (key === 'all') return props.users.map((u: any) => u.id)
  if (key === 'free') return freeInRange(props.users, props.bookings, dateFrom.value, dateTo.value)
  return bookedInRange(props.bookings, Number(key.slice(2)), dateFrom.value, dateTo.value)
}

// --- Ergebnis der Kriterien plus manueller Abweichungen --------------------

const ruleIds = computed(() => {
  const ids = new Set<number>()
  for (const key of activeCriteria.value) {
    for (const id of idsForCriterion(key)) ids.add(id)
  }
  return ids
})

const selectedIds = computed(() => {
  const ids = new Set<number>()
  for (const id of ruleIds.value) ids.add(id)
  for (const id of manualAdded.value) ids.add(id)
  return ids
})

function isCriterionActive(key: CriterionKey): boolean {
  return activeCriteria.value.has(key)
}

/**
 * Was der Filter gerade tut, in Worten (TIM-47). Aus den Kaestchen allein ist
 * das nur zu erschliessen — und ob ueberhaupt ein eigener Filter gilt, sieht man
 * sonst nur am ausgegrauten Zuruecksetzen-Knopf.
 *
 * Die Bausteine liegen in einem Set: das eigene Projekt als Kaestchen und die
 * Dropdown-Option "Gebucht" bedeuten dasselbe und ergeben denselben Text, der
 * damit nur einmal erscheint.
 */
const filterSummary = computed(() => {
  if (activeCriteria.value.has('all')) return 'Alle Personen'

  const labels = new Set<string>()
  for (const key of activeCriteria.value) {
    if (key === 'free') {
      labels.add('frei')
      continue
    }
    const p = props.projects.find((x: any) => x.id === Number(key.slice(2)))
    if (p) labels.add(`in ${p.name} gebucht`)
  }

  const extra = [...manualAdded.value].filter(id => !ruleIds.value.has(id)).length
  if (labels.size === 0 && extra === 0) return 'Niemand ausgewählt'

  const parts: string[] = []
  if (labels.size > 0) parts.push([...labels].join(' oder '))
  if (extra > 0) parts.push(`${extra} einzeln gewählt`)
  const text = `${parts.join(' · ')} · ${toGerman(dateFrom.value, '.')} – ${toGerman(dateTo.value, '.')}`
  return text.charAt(0).toUpperCase() + text.slice(1)
})

// Die Projektfarbe steckt im Farbpunkt, nicht in der Schrift: mehrere Projekte
// sind so hell (>TBD ist #f0fff0), dass sie als Text auf Weiss unlesbar waeren.
// Der duenne dunkle Rand haelt auch solche Punkte sichtbar.
function projectDotStyle(p: any) {
  return { background: p.color, border: '1px solid rgba(0,0,0,0.25)' }
}

function toggleCriterion(key: CriterionKey) {
  wantsDefault.value = false
  const next = new Set(activeCriteria.value)
  if (next.has(key)) {
    // Abhaken nimmt die Personen dieses Kriteriums wieder weg — sie bleiben nur,
    // wenn ein anderes Kaestchen sie ebenfalls liefert oder sie einzeln
    // angeklickt wurden.
    next.delete(key)
  } else {
    next.add(key)
    // Was das Kriterium liefert, muss nicht zusaetzlich als manuell mitgefuehrt
    // werden.
    for (const id of idsForCriterion(key)) manualAdded.value.delete(id)
  }
  activeCriteria.value = next
}

/**
 * Weicht die Auswahl von einem angehakten Kaestchen ab, verschwindet dessen
 * Haken — die bisher ausgewaehlten Personen bleiben aber stehen. Es gibt bewusst
 * keinen dritten Zustand am Haekchen (Hoffi am 11.08.: "Auswahl über Kästchen =
 * Haken / Veränderung = Haken weg"). Björns Einwand dazu: ein Zwischenzustand
 * waere hier nur in einer Richtung konsistent, anders als in CAD/BIM.
 */
function dissolveCriteriaContaining(userId: number) {
  const next = new Set(activeCriteria.value)
  let changed = false
  for (const key of activeCriteria.value) {
    const ids = idsForCriterion(key)
    if (!ids.includes(userId)) continue
    for (const id of ids) manualAdded.value.add(id)
    next.delete(key)
    changed = true
  }
  if (changed) activeCriteria.value = next
}

function toggleUser(userId: number) {
  wantsDefault.value = false
  if (selectedIds.value.has(userId)) {
    dissolveCriteriaContaining(userId)
    manualAdded.value.delete(userId)
  } else {
    // Eine Person dazunehmen laesst die Kaestchen unberuehrt: deren Personen
    // sind ja weiterhin vollstaendig ausgewaehlt.
    manualAdded.value.add(userId)
  }
}

function selectAll() {
  wantsDefault.value = false
  activeCriteria.value = new Set(['all'])
  manualAdded.value = new Set()
}

function deselectAll() {
  wantsDefault.value = false
  activeCriteria.value = new Set()
  manualAdded.value = new Set()
}

// Das Enddatum darf nicht vor dem Anfang liegen; sonst waere der Zeitraum leer
// und "Frei" lieferte schlagartig alle Personen.
function setDateFrom(german: string) {
  wantsDefault.value = false
  const iso = fromGerman(german)
  if (!iso) return
  dateFrom.value = iso
  if (dateTo.value < iso) dateTo.value = iso
}

function setDateTo(german: string) {
  wantsDefault.value = false
  const iso = fromGerman(german)
  if (!iso) return
  dateTo.value = iso < dateFrom.value ? dateFrom.value : iso
}

// Gespeichert wird die Regel, nicht ihr Ergebnis.
function toggleCategory(key: string) {
  const s = new Set(visibleCategories.value)
  s.has(key) ? s.delete(key) : s.add(key)
  visibleCategories.value = s
}

// null, wenn alle Kategorien an sind: dann steht kein eigener Wunsch dahinter,
// und eine spaeter hinzukommende Kategorie ist automatisch sichtbar.
function categoriesForFilter(): string[] | null {
  const alle = Object.keys(CATEGORIES)
  const gewaehlt = alle.filter(k => visibleCategories.value.has(k))
  return gewaehlt.length === alle.length ? null : gewaehlt
}

function buildFilter(): ResourceFilter {
  if (wantsDefault.value) return { ...defaultFilter(), categories: categoriesForFilter() }
  const today = todayIso()
  return {
    v: FILTER_VERSION,
    mode: 'custom',
    all: activeCriteria.value.has('all'),
    projects: [...activeCriteria.value].filter(k => k.startsWith('p:')).map(k => Number(k.slice(2))),
    range: rangeFrom(dateFrom.value, dateTo.value, today),
    free: activeCriteria.value.has('free'),
    added: [...manualAdded.value],
    categories: categoriesForFilter(),
  }
}

// Zurueck auf den Standard: das Fenster bleibt offen und zeigt wieder die
// Vorbelegung. Verworfen wird der eigene Filter erst mit "Anwenden" — jede
// Aenderung davor macht daraus wieder eine eigene Auswahl.
function resetToDefaultSelection() {
  resetToDefaults()
  wantsDefault.value = true
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      @click.self="emit('cancel')"
    >
      <div class="bg-white rounded-lg shadow-xl flex flex-col w-[750px] max-w-[95vw] max-h-[85vh]" @click.stop>

        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 class="text-lg font-semibold">
            <span :style="{ color: project.color }">{{ project.name }}</span>
            &nbsp;Personen wählen
          </h2>
          <button class="opacity-60 hover:opacity-100 transition-opacity" @click="emit('cancel')">
            <X :size="20" />
          </button>
        </div>

        <!-- Kriterien: Projekte und "Frei". Alle verknüpfen sich als ODER. -->
        <div class="px-6 py-3 border-b shrink-0">
          <div class="flex flex-wrap items-center gap-x-5 gap-y-2">
            <label
              v-for="p in projectsWithBookings"
              :key="p.id"
              class="flex items-center gap-1.5 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                :checked="isCriterionActive(`p:${p.id}`)"
                class="cursor-pointer"
                @change="toggleCriterion(`p:${p.id}`)"
              />
              <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="projectDotStyle(p)"></span>
              <span class="text-sm font-medium">{{ p.name }}</span>
            </label>

            <!-- "Frei" ist kein Projekt, sondern die Verneinung jeder Zugehörigkeit
                 und steht nirgends in der Datenbank. Deshalb hinter einem Trenner
                 und mit hohlem statt gefülltem Punkt. -->
            <span class="w-px self-stretch bg-gray-200" aria-hidden="true"></span>
            <label class="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                :checked="isCriterionActive('free')"
                class="cursor-pointer"
                @change="toggleCriterion('free')"
              />
              <span class="w-2.5 h-2.5 rounded-full shrink-0 border border-dashed border-gray-400"></span>
              <span class="text-sm font-medium">Frei</span>
            </label>
          </div>
        </div>

        <!-- Zeitraum: gilt für alle Kriterien -->
        <div class="px-6 py-3 border-b shrink-0">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span class="text-sm text-gray-600">Zeitraum</span>
            <DateInput label="von" :modelValue="toGerman(dateFrom)" @datechange="setDateFrom" />
            <DateInput label="bis" :modelValue="toGerman(dateTo)" @datechange="setDateTo" />
          </div>
        </div>

        <!-- Kategorien der Unterprojekte (TIM-73). Waehlt Zeilen, nicht Personen -
             gilt deshalb unabhaengig vom Standard-/Eigen-Modus oben. -->
        <div v-if="hasSubprojects" class="px-6 py-3 border-b shrink-0">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span class="text-sm text-gray-600">Bereiche zeigen</span>
            <label v-for="(label, key) in CATEGORIES" :key="key"
                   class="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" class="cursor-pointer shrink-0"
                     :checked="visibleCategories.has(key)"
                     @change="toggleCategory(key)" />
              <span class="text-sm">{{ label }}</span>
            </label>
          </div>
        </div>

        <!-- Aktionen + Suche -->
        <div class="flex items-center justify-between px-6 py-3 border-b shrink-0 gap-4">
          <div class="flex gap-4 text-sm">
            <button class="hover:underline" @click="selectAll">Alle</button>
            <button class="hover:underline" @click="deselectAll">Alle abwählen</button>
            <button
              class="hover:underline disabled:no-underline disabled:opacity-40 disabled:cursor-default"
              :disabled="!hasOwnFilter || wantsDefault"
              :title="hasOwnFilter ? 'Eigenen Filter verwerfen' : 'Dieses Projekt steht bereits auf dem Standard'"
              @click="resetToDefaultSelection"
            >Auf Standard zurücksetzen</button>
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <!-- Aus einer Suche kam man bisher nur heraus, indem man das Feld
                 von Hand leerte. Der Link erscheint deshalb nur, solange gesucht wird. -->
            <button
              v-if="search"
              class="text-sm text-gray-500 hover:text-gray-700 hover:underline whitespace-nowrap"
              @click="search = ''"
            >Liste anzeigen</button>
            <div class="relative">
              <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                v-model="search"
                placeholder="Personen suchen"
                class="pl-8 h-8 border rounded-md text-sm w-48 pr-3 focus:outline-none focus:ring-1 focus:ring-gray-300"
              />
            </div>
          </div>
        </div>

        <!-- Was der Filter gerade tut, in Worten -->
        <div class="flex items-baseline justify-between gap-4 px-6 pt-3 pb-1 shrink-0 text-xs text-gray-500">
          <span class="truncate">{{ filterSummary }}</span>
          <span class="shrink-0 tabular-nums">{{ selectedIds.size }} von {{ users.length }}</span>
        </div>

        <!-- Personengrid -->
        <div class="grid grid-cols-4 gap-x-4 gap-y-1 px-6 pb-4 pt-1 overflow-y-auto flex-1">
          <label
            v-for="u in sortedFilteredUsers"
            :key="u.id"
            class="flex items-center gap-2 cursor-pointer py-0.5 select-none"
          >
            <input
              type="checkbox"
              :checked="selectedIds.has(u.id)"
              class="cursor-pointer shrink-0"
              @change="toggleUser(u.id)"
            />
            <span class="text-sm truncate">{{ u.firstname }} {{ u.lastname }} ({{ u.short }})</span>
          </label>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-3 px-6 py-4 border-t shrink-0">
          <Button variant="outline" @click="emit('cancel')">Abbrechen</Button>
          <Button
            class="bg-green-500 hover:bg-green-600 text-white"
            @click="emit('apply', buildFilter())"
          >Anwenden</Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
